/**
 * AdminCommandPalette.jsx
 * Global keyboard-accessible command palette (Ctrl+K / Cmd+K).
 * No external fuzzy-search library — pure JS subsequence + transposition matching.
 *
 * Props:
 *   isOpen    {boolean}
 *   onClose   {() => void}
 *   onNavigate {(path: string) => void}
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Search index ────────────────────────────────────────────────────────────

const SEARCH_INDEX = [
  // Agent Tools
  { icon: '📍', label: 'Assignment Mode',  path: '/assignment',                section: 'Agent Tools',    desc: 'Map-based venue assignment',                aliases: ['assign', 'map', 'venue assign', 'agent'] },
  { icon: '🗺️', label: 'Quick Onboard',    path: '/assignment?mode=onboard',   section: 'Agent Tools',    desc: 'Google Maps onboarding',                    aliases: ['onboard', 'google', 'maps', 'new venue'] },
  { icon: '🗺️', label: 'Assignment V2',    path: '/assignment-v2',             section: 'Agent Tools',    desc: 'New Mapbox assignment map',                 aliases: ['assign v2', 'mapbox', 'filter', 'venues map'] },
  { icon: '⬡',  label: 'Boundary Manager', path: '/boundary-manager',          section: 'Agent Tools',    desc: 'Draw & edit venue property polygons',       aliases: ['polygon', 'boundary', 'draw', 'property'] },
  { icon: '🏘️', label: 'Zones Manager',    path: '/zones-manager',             section: 'Agent Tools',    desc: 'View & manage venue zones',                 aliases: ['zone', 'ac', 'seating', 'noise', 'areas'] },
  { icon: '🏷️', label: 'Venue Types',      path: '/venue-type-manager',        section: 'Agent Tools',    desc: 'Assign venue type taxonomy',                aliases: ['type', 'taxonomy', 'cafe', 'restaurant', 'bar'] },
  // Media
  { icon: '🖼️', label: 'Media Manager',    path: '/media-manager',             section: 'Media',          desc: 'Browse & approve photos',                   aliases: ['photo', 'image', 'approve', 'pending', 'media'] },
  { icon: '📸', label: 'Bulk Upload',       path: '/bulk-upload',               section: 'Media',          desc: 'GPS-grouped bulk photo upload',             aliases: ['upload', 'bulk', 'photos', 'gps', 'import'] },
  // Schema Managers
  { icon: '🏷️', label: 'Venue Schema',     path: '/venue-schema',              section: 'Schema Managers', desc: 'Category / type taxonomy tree',            aliases: ['schema', 'category', 'group', 'taxonomy', 'tree'] },
  { icon: '📋', label: 'Event Schema',      path: '/event-schema',              section: 'Schema Managers', desc: 'Event type taxonomy ETC1/ETC2/L3',         aliases: ['event type', 'etc1', 'etc2', 'l3', 'taxonomy'] },
  { icon: '🏅', label: 'Venue Lifecycle',   path: '/venue-lifecycle',           section: 'Schema Managers', desc: 'Verification levels & state machine',      aliases: ['lifecycle', 'state', 'verification', 'status', 'level'] },
  { icon: '🍽️', label: 'Digital Menus',     path: '/menu-manager',              section: 'Schema Managers', desc: 'OCR menu extraction & editing',            aliases: ['menu', 'ocr', 'digital', 'extract', 'food', 'drinks'] },
  // Events
  { icon: '📅', label: 'Events Manager',    path: '/events-manager',            section: 'Events',         desc: 'Create & manage events',                    aliases: ['event', 'create event', 'calendar', 'schedule'] },
  { icon: '📺', label: 'Match Screenings',  path: '/match-screenings',          section: 'Events',         desc: 'Assign venues to World Cup matches',          aliases: ['world cup', 'screening', 'football', 'sports bar', 'wc2026', 'match'] },
  // Content
  { icon: '📝', label: 'Notes',             path: '/venue-notes',               section: 'Content',        desc: 'Field agent venue notes',                   aliases: ['note', 'field note', 'agent note', 'comment', 'observation'] },
  { icon: '⭐', label: 'Reviews',           path: '/reviews-manager',           section: 'Content',        desc: 'Moderate & browse venue reviews',           aliases: ['review', 'rating', 'star', 'feedback', 'moderate'] },
  { icon: '🌐', label: 'Translations',      path: '/translation-cms',           section: 'Content',        desc: 'i18n content management',                   aliases: ['translate', 'i18n', 'language', 'thai', 'locale', 'string'] },
  // WiFi
  { icon: '📶', label: 'WiFi Speed Tests',  path: '/wifi-tests',                section: 'WiFi',           desc: '505 tests across 158 venues',               aliases: ['wifi', 'speed', 'test', 'internet', 'mbps', 'bandwidth'] },
  // AI / ML
  { icon: '🤖', label: 'AI Prompts',        path: '/classification-prompt',     section: 'AI / ML',        desc: 'Classification rules & testing',            aliases: ['ai', 'ml', 'prompt', 'rule', 'classify', 'model'] },
  // Users & Access
  { icon: '👤', label: 'User Manager',      path: '/users',                     section: 'Users & Access', desc: 'Roles, access control & invites',           aliases: ['user', 'role', 'admin', 'agent', 'invite', 'access'] },
  // Commerce
  { icon: '🎁', label: 'Offers',            path: '/offers',                    section: 'Commerce',       desc: 'Promotions & redemptions',                  aliases: ['offer', 'promo', 'promotion', 'discount', 'deal'] },
  { icon: '🛒', label: 'Street Vendors',    path: '/street-vendors',            section: 'Commerce',       desc: 'Vendor tracking & approval',                aliases: ['vendor', 'street', 'market', 'stall'] },
  // BI
  { icon: '📊', label: 'Dashboard KPI',     path: '/bi-dashboard',              section: 'BI',             desc: 'Venue & user stats overview',               aliases: ['bi', 'kpi', 'stats', 'analytics', 'dashboard', 'metrics'] },
];

// ─── Fuzzy search ─────────────────────────────────────────────────────────────

/**
 * Returns true if all characters of `needle` appear in order inside `haystack`
 * (case-insensitive subsequence match).
 */
function isSubsequence(needle, haystack) {
  let ni = 0;
  for (let hi = 0; hi < haystack.length && ni < needle.length; hi++) {
    if (haystack[hi] === needle[ni]) ni++;
  }
  return ni === needle.length;
}

/**
 * Generate all single-transposition variants of a string (swap adjacent chars).
 * Used to tolerate one typo.
 */
function transpositionVariants(s) {
  const variants = [];
  for (let i = 0; i < s.length - 1; i++) {
    variants.push(s.slice(0, i) + s[i + 1] + s[i] + s.slice(i + 2));
  }
  return variants;
}

/**
 * Score an item against a query string.
 * Returns 0 if no match, otherwise a positive score (higher = better).
 */
function scoreItem(item, q) {
  if (!q) return 1; // show all when empty

  const lq = q.toLowerCase().trim();
  const lLabel = item.label.toLowerCase();
  const lDesc = item.desc.toLowerCase();
  const lAliases = (item.aliases || []).join(' ').toLowerCase();
  const lSection = item.section.toLowerCase();
  const combined = `${lLabel} ${lDesc} ${lAliases} ${lSection}`;

  // Direct exact-ish checks first (highest score)
  if (lLabel.startsWith(lq)) return 10;
  if (lLabel.includes(lq)) return 6;
  if (lAliases.includes(lq)) return 4;
  if (lDesc.includes(lq)) return 3;

  // Subsequence match on combined text
  if (isSubsequence(lq, combined)) return 2;

  // Typo tolerance: try transpositions for queries of 3+ chars
  if (lq.length >= 3) {
    const variants = transpositionVariants(lq);
    for (const v of variants) {
      if (lLabel.includes(v) || lAliases.includes(v) || lDesc.includes(v)) return 1;
      if (isSubsequence(v, combined)) return 1;
    }
  }

  return 0;
}

const MAX_RESULTS = 12;
const DEFAULT_PER_SECTION = 6;

function buildResults(query) {
  const q = query.trim();

  if (!q) {
    // Empty query: show first DEFAULT_PER_SECTION items from each section, grouped
    const sectionMap = new Map();
    for (const item of SEARCH_INDEX) {
      if (!sectionMap.has(item.section)) sectionMap.set(item.section, []);
      const arr = sectionMap.get(item.section);
      if (arr.length < DEFAULT_PER_SECTION) arr.push(item);
    }
    const results = [];
    for (const [, items] of sectionMap) {
      results.push(...items);
    }
    return results;
  }

  // Scored search
  const scored = SEARCH_INDEX
    .map(item => ({ item, score: scoreItem(item, q) }))
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, MAX_RESULTS)
    .map(x => x.item);

  return scored;
}

// ─── Section badge colour map ─────────────────────────────────────────────────

const SECTION_BADGE_COLORS = {
  'Agent Tools':    { bg: '#1e3a8a', color: '#60a5fa' },
  'Media':          { bg: '#0e4a5a', color: '#22d3ee' },
  'Schema Managers':{ bg: '#064e3b', color: '#4ade80' },
  'Events':         { bg: '#451a03', color: '#fbbf24' },
  'Content':        { bg: '#451a03', color: '#fbbf24' },
  'WiFi':           { bg: '#2e1065', color: '#a78bfa' },
  'AI / ML':        { bg: '#500724', color: '#f472b6' },
  'Users & Access': { bg: '#0c2a3f', color: '#38bdf8' },
  'Commerce':       { bg: '#4c0519', color: '#fb7185' },
  'BI':             { bg: '#1f2937', color: '#6b7280' },
};

function SectionBadge({ section }) {
  const colors = SECTION_BADGE_COLORS[section] || { bg: '#1e293b', color: '#94a3b8' };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '.06em',
      textTransform: 'uppercase', padding: '2px 6px',
      borderRadius: 4, background: colors.bg, color: colors.color,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {section}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AdminCommandPalette({ isOpen, onClose, onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const overlayRef = useRef(null);

  // Recompute results whenever query changes
  useEffect(() => {
    const newResults = buildResults(query);
    setResults(newResults);
    setSelectedIndex(0);
  }, [query]);

  // When palette opens: autofocus input, reset query
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      // Small delay so the DOM is visible before focus
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 30);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.querySelector('[data-selected="true"]');
    if (selected) selected.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Keyboard navigation inside the palette
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;

    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => (i + 1) % Math.max(results.length, 1));
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => (i - 1 + Math.max(results.length, 1)) % Math.max(results.length, 1));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const item = results[selectedIndex];
      if (item) {
        onNavigate(item.path);
      }
      return;
    }
  }, [isOpen, results, selectedIndex, onNavigate, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Click outside overlay to close
  const handleOverlayClick = useCallback((e) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  // Group results by section (preserves order)
  const grouped = [];
  let lastSection = null;
  for (const item of results) {
    if (item.section !== lastSection) {
      grouped.push({ type: 'header', section: item.section });
      lastSection = item.section;
    }
    grouped.push({ type: 'item', item });
  }

  // Flat index of items only (for selectedIndex mapping)
  const flatItems = results;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 80,
      }}
    >
      <div
        style={{
          background: '#1e293b',
          border: '1px solid #334155',
          borderRadius: 12,
          width: 560,
          maxHeight: 480,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
          overflow: 'hidden',
        }}
      >
        {/* Search input row */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 16px',
          borderBottom: '1px solid #334155',
          flexShrink: 0,
        }}>
          <span style={{ fontSize: 15, color: '#475569', flexShrink: 0 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search screens, tools, features..."
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#e2e8f0',
              fontSize: 16,
              padding: '14px 0',
              fontFamily: 'inherit',
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#475569', fontSize: 13, padding: '2px 4px',
                borderRadius: 4, flexShrink: 0,
              }}
              title="Clear"
            >
              ✕
            </button>
          )}
        </div>

        {/* Results list */}
        <div
          ref={listRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {flatItems.length === 0 ? (
            <div style={{
              padding: '32px 16px',
              textAlign: 'center',
              color: '#475569',
              fontSize: 13,
            }}>
              No results for "{query}"
            </div>
          ) : (
            grouped.map((entry, gi) => {
              if (entry.type === 'header') {
                return (
                  <div
                    key={`header-${entry.section}`}
                    style={{
                      fontSize: 10,
                      letterSpacing: '.08em',
                      color: '#475569',
                      padding: '8px 14px 3px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                    }}
                  >
                    {entry.section}
                  </div>
                );
              }

              // type === 'item'
              const itemIndex = flatItems.indexOf(entry.item);
              const isSelected = itemIndex === selectedIndex;

              return (
                <ResultItem
                  key={`${entry.item.path}-${gi}`}
                  item={entry.item}
                  isSelected={isSelected}
                  query={query}
                  onNavigate={onNavigate}
                  onHover={() => setSelectedIndex(itemIndex)}
                />
              );
            })
          )}
        </div>

        {/* Footer hint */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          padding: '6px 14px',
          borderTop: '1px solid #1e293b',
          background: '#162032',
          flexShrink: 0,
        }}>
          <KbdHint keys="↑↓" label="navigate" />
          <KbdHint keys="↵" label="open" />
          <KbdHint keys="esc" label="close" />
        </div>
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ResultItem({ item, isSelected, query, onNavigate, onHover }) {
  return (
    <div
      data-selected={isSelected ? 'true' : 'false'}
      onMouseEnter={onHover}
      onClick={() => onNavigate(item.path)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 14px',
        cursor: 'pointer',
        background: isSelected ? '#1e3a5f' : 'transparent',
        borderLeft: isSelected ? '3px solid #60a5fa' : '3px solid transparent',
        transition: 'background 0.08s',
      }}
    >
      {/* Icon */}
      <span style={{ fontSize: 16, flexShrink: 0, width: 22, textAlign: 'center' }}>
        {item.icon}
      </span>

      {/* Label + description */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13,
          fontWeight: 600,
          color: isSelected ? '#e2e8f0' : '#cbd5e1',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {item.label}
        </div>
        <div style={{
          fontSize: 11,
          color: '#475569',
          marginTop: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {item.desc}
        </div>
      </div>

      {/* Section badge */}
      <SectionBadge section={item.section} />
    </div>
  );
}

function KbdHint({ keys, label }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#475569', fontSize: 10 }}>
      <kbd style={{
        fontSize: 9,
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 3,
        padding: '2px 5px',
        color: '#64748b',
        fontFamily: 'inherit',
        letterSpacing: '.02em',
      }}>
        {keys}
      </kbd>
      <span>{label}</span>
    </span>
  );
}
